import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  TableCell,
  TableRow,
  Typography
} from '@material-ui/core';
import { imgBaseUrl } from 'configs';
import { TextFormattedNumber } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import { singleUnitName } from 'helpers/other';
import round from 'helpers/mathRound';
import { addDays } from 'date-fns';
import { useStyles } from '../styles';
import { calculateItemData } from './utils/calculateItemData';
import ProgressItemSymbol from './ProgressItemSymbol';


const QuoteSeriesItemRow = ({item, itemIndex, seriesIndex, isLastSeries, isLastItem}) => {
  const classes = useStyles();
  const [loaded, setLoaded] = useState(false);

  const { customerPushCart } = useSelector((state: IReduxState) => state.quote);
  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  const { sku, qty, custom_subscription_price } = item;
  const product = customerPushCart?.products[item.sku]?.product || {};
  const {
    name = '',
    image = '',
    subscription_active = '',
    subscription_label = '',
  } = product;

  const {
    savingsValue,
    unitsName,
    quoteFrequency,
    subscriptionData,
    subtotal,
    discount,
    amount,
    itemPrice,
    updatedPrice
  } = calculateItemData(item, customerPushCart.products[sku]?.product);

  const {
    quotePlan
  } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    setLoaded(false);
  }, [image]);

  const {startDate, expirationDate, mustUsedDate} = quotePlan[seriesIndex] || {};

  return (
    <TableRow className={isLastItem ? classes.noBorder : ''}>
      <TableCell width="100">
        <img
          src={loaded ? `${mediaStoreUrl}${image}` : logo}
          alt={`${name} image`}
          className={classes.quoteSummaryTableImage}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            (e.target as HTMLImageElement).onerror = null;
            (e.target as HTMLImageElement).src = logo;
          }}
        />
      </TableCell>
      <TableCell width="270">
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>
          {name}&nbsp;
          <span className="text-13 font-italic font-medium whitespace-no-wrap">
            (<TextFormattedNumber value={round(updatedPrice ? updatedPrice : itemPrice - savingsValue)} prefix="$" />/
            {singleUnitName(unitsName)})
          </span>
        </Typography>
        {subscription_active === '1' && !!subscriptionData?.frequency_count &&
          <>
            <div className="flex flex-wrap">
              <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
                {subscription_label}{':'}
              </span>
              <span className="text-13 whitespace-no-wrap font-italic">
                {subscriptionData?.sproduct_label || ''}
              </span>
            </div>
            <div className="flex flex-wrap mb-2">
              <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
                {'Periodic Price:'}
              </span>
              <span className="text-13 whitespace-no-wrap font-italic">
                <TextFormattedNumber
                  value={custom_subscription_price
                    ? custom_subscription_price
                    : round(subscriptionData?.installment_price) || 0
                  }
                  prefix="$"
                />
              </span>
            </div>
          </>
        }
        <div>
          <strong>{'Start Date: '}</strong>
          <span>
            {new Date(startDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
            }
          </span>
          {addDays(new Date(startDate), 1) < new Date() &&
          <div className="text-error">
            <span>
              * Start Date can&apos;t be past
            </span>
          </div>}
        </div>

        <div>
          <strong>{'Expiration Date: '}</strong>
          <span>
            {new Date(expirationDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
            }
          </span>
          {new Date(expirationDate) < new Date() &&
          <div className="text-error">
            <span>
              * Expiration Date can&apos;t be past
            </span>
          </div>}
        </div>

        {mustUsedDate &&
        <div>
          <strong>{'Must Used Date: '}</strong>
          <span>
            {new Date(mustUsedDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
            }
          </span>
          {new Date(mustUsedDate) < new Date() &&
          <div className="text-error">
            <span>
              * Must Used Date can&apos;t be past
            </span>
          </div>}
        </div>}
      </TableCell>
      <TableCell padding="none" className="h-full relative" width="80">
        <ProgressItemSymbol
          isFirst={itemIndex === 0 && seriesIndex === 0}
          isPoint={itemIndex === 0}
          isLast={isLastItem && isLastSeries && (quotePlan.length !== 1 || isLastItem)}
        />
      </TableCell>
      <TableCell>
        <Typography
          variant="subtitle2"
          className="font-semibold"
          gutterBottom
        >
          Quantity
        </Typography>
        <Typography variant="body2">
          {`${qty} ${
            qty === 1 ? singleUnitName(unitsName) : unitsName
          }`}
        </Typography>
        {!!quoteFrequency &&
        <>
          <Typography
            variant="subtitle2"
            className="font-semibold"
            gutterBottom
          >
            Frequency
          </Typography>
          <Typography variant="body2">
            {`${quoteFrequency} ${quoteFrequency === 1
              ? singleUnitName(unitsName)
              : unitsName} `
            }
            <span className="whitespace-no-wrap">per year</span>
          </Typography>
        </>
        }
      </TableCell>
      <TableCell>
        <Typography
          variant="subtitle2"
          className="font-semibold"
          gutterBottom
        >
          Subtotal
        </Typography>
        <TextFormattedNumber
          value={subtotal * qty}
          prefix="$"
        />
      </TableCell>
      <TableCell>
        <Typography
          variant="subtitle2"
          className="font-semibold"
          gutterBottom
        >
          Savings
        </Typography>
        <TextFormattedNumber
          value={discount}
          prefix="$"
        />
      </TableCell>
      <TableCell className="font-semibold">
        <Typography
          variant="subtitle2"
          className="font-semibold"
          gutterBottom
        >
          TOTAL
        </Typography>
        <TextFormattedNumber
          value={amount}
          prefix="$"
        />
      </TableCell>
    </TableRow>
  );
};

export default QuoteSeriesItemRow;
